import {
  CanvasElementType,
  EdgelessCRUDIdentifier,
  getSurfaceBlock,
  getSurfaceComponent,
  isNoteBlock,
  Overlay,
  OverlayIdentifier,
  type RoughCanvas,
} from '@blocksuite/affine-block-surface';
import {
  type ConnectionOverlay,
  ConnectorPathGenerator,
} from '@blocksuite/affine-gfx-connector';
import { mountShapeTextEditor } from '@blocksuite/affine-gfx-shape';
import type {
  Connection,
  ConnectorElementModel,
  NoteBlockModel,
  ShapeType,
} from '@blocksuite/affine-model';
import {
  DEFAULT_NOTE_HEIGHT,
  DefaultTheme,
  LayoutType,
  MindmapElementModel,
  ShapeElementModel,
  shapeMethods,
} from '@blocksuite/affine-model';
import { ToolbarRegistryIdentifier } from '@blocksuite/affine-shared/services';
import type { SelectedRect } from '@blocksuite/affine-shared/types';
import { handleNativeRangeAtPoint } from '@blocksuite/affine-shared/utils';
import { DisposableGroup } from '@blocksuite/global/disposable';
import type { Bound, IVec } from '@blocksuite/global/gfx';
import { Vec } from '@blocksuite/global/gfx';
import { WithDisposable } from '@blocksuite/global/lit';
import {
  ArrowUpBigIcon,
  PlusIcon,
  SiblingNodeIcon,
  SubNodeIcon,
} from '@blocksuite/icons/lit';
import {
  type BlockComponent,
  type BlockStdScope,
  stdContext,
} from '@blocksuite/std';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';
import { consume } from '@lit/context';
import { css, html, LitElement, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';

import { EdgelessAutoCompletePanel } from './auto-complete-panel.js';
import {
  createEdgelessElement,
  Direction,
  getPosition,
  isShape,
  MAIN_GAP,
  nextBound,
} from './utils.js';

/** The mind-map layouts that grow along the vertical axis. */
const VERTICAL_MINDMAP_LAYOUTS: ReadonlySet<LayoutType> = new Set([
  LayoutType.TOP,
  LayoutType.BOTTOM,
  LayoutType.BALANCE_VERTICAL,
]);

class AutoCompleteOverlay extends Overlay {
  linePoints: IVec[] = [];

  renderShape: ((ctx: CanvasRenderingContext2D) => void) | null = null;

  stroke = '';

  override render(ctx: CanvasRenderingContext2D, _rc: RoughCanvas) {
    if (this.linePoints.length && this.renderShape) {
      ctx.setLineDash([2, 2]);
      ctx.strokeStyle = this.stroke;
      ctx.beginPath();
      this.linePoints.forEach((p, index) => {
        if (index === 0) ctx.moveTo(p[0], p[1]);
        else ctx.lineTo(p[0], p[1]);
      });
      ctx.stroke();

      this.renderShape(ctx);
      ctx.stroke();
    }
  }
}

export class EdgelessAutoComplete extends WithDisposable(LitElement) {
  static override styles = css`
    .edgeless-auto-complete-container {
      position: absolute;
      z-index: 1;
      pointer-events: none;
    }
    .edgeless-auto-complete-arrow-wrapper {
      width: 72px;
      height: 44px;
      position: absolute;
      z-index: 1;
      pointer-events: auto;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .edgeless-auto-complete-arrow-wrapper.hidden {
      display: none;
    }
    .edgeless-auto-complete-arrow {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border-radius: 19px;
      cursor: pointer;
      pointer-events: auto;
      transition:
        background 0.3s linear,
        box-shadow 0.2s linear;
    }
    .edgeless-auto-complete-arrow-wrapper.mindmap {
      width: 26px;
      height: 26px;
    }

    .edgeless-auto-complete-arrow-wrapper:hover
      > .edgeless-auto-complete-arrow {
      border: 1px solid var(--affine-border-color);
      box-shadow: var(--affine-shadow-1);
      background: var(--affine-white);
    }

    .edgeless-auto-complete-arrow-wrapper
      > .edgeless-auto-complete-arrow:hover {
      border: 1px solid var(--affine-white-10);
      box-shadow: var(--affine-shadow-1);
      background: var(--affine-primary-color);
    }

    .edgeless-auto-complete-arrow-wrapper.mindmap
      > .edgeless-auto-complete-arrow {
      border: 1px solid var(--affine-border-color);
      box-shadow: var(--affine-shadow-1);
      background: var(--affine-white);

      transition:
        background 0.3s linear,
        color 0.2s linear;
    }

    .edgeless-auto-complete-arrow-wrapper.mindmap
      > .edgeless-auto-complete-arrow:hover {
      border: 1px solid var(--affine-white-10);
      box-shadow: var(--affine-shadow-1);
      background: var(--affine-primary-color);
    }

    .edgeless-auto-complete-arrow svg {
      fill: #77757d;
      color: #77757d;
    }
    .edgeless-auto-complete-arrow:hover svg {
      fill: #ffffff;
      color: #ffffff;
    }
  `;

  private get _surface() {
    return getSurfaceBlock(this.std.store);
  }

  private _autoCompleteOverlay!: AutoCompleteOverlay;

  private readonly _onPointerDown = (e: PointerEvent, type: Direction) => {
    const viewportRect = this.gfx.viewport.boundingClientRect;
    const start = this.gfx.viewport.toModelCoord(
      e.clientX - viewportRect.left,
      e.clientY - viewportRect.top
    );

    if (!this.edgeless.std.event) return;

    let connector: ConnectorElementModel | null;

    this._disposables.addFromEvent(document, 'pointermove', e => {
      const point = this.gfx.viewport.toModelCoord(
        e.clientX - viewportRect.left,
        e.clientY - viewportRect.top
      );
      if (Vec.dist(start, point) > 8 && !this._isMoving) {
        if (!this.canShowAutoComplete) return;
        this._isMoving = true;
        const { startPosition } = getPosition(type);
        connector = this._addConnector(
          {
            id: this.current.id,
            position: startPosition,
          },
          {
            position: point,
          }
        );
      }
      if (this._isMoving) {
        if (!connector) {
          return;
        }
        const otherSideId = connector.source.id;

        connector.target = this.connectionOverlay.renderConnector(
          point,
          otherSideId ? [otherSideId] : []
        );

        this.std.get(ToolbarRegistryIdentifier).flags.hide();
      }
    });

    this._disposables.addFromEvent(document, 'pointerup', e => {
      if (!this._isMoving) {
        this._generateElementOnClick(type);
      } else if (connector && !connector.target.id) {
        this.gfx.selection.clear();
        this._createAutoCompletePanel(e, connector);
      }

      this.std.get(ToolbarRegistryIdentifier).flags.show();

      this._isMoving = false;
      this.connectionOverlay.clear();
      this._disposables.dispose();
      this._disposables = new DisposableGroup();
    });
  };

  private _pathGenerator!: ConnectorPathGenerator;

  private _timer: ReturnType<typeof setTimeout> | null = null;

  get canShowAutoComplete() {
    const { current } = this;
    return isShape(current) || isNoteBlock(current);
  }

  get connectionOverlay() {
    return this.std.get(OverlayIdentifier('connection')) as ConnectionOverlay;
  }

  get crud() {
    return this.std.get(EdgelessCRUDIdentifier);
  }

  get gfx() {
    return this.std.get(GfxControllerIdentifier);
  }

  private _addConnector(source: Connection, target: Connection) {
    const id = this.crud.addElement(CanvasElementType.CONNECTOR, {
      source,
      target,
    });
    if (!id) return null;
    return this.crud.getElementById(id) as ConnectorElementModel;
  }

  /**
   * @param bucket For a child of the ROOT, which side to put it on: the RIGHT
   *   bucket (right of the root, or below it on the vertical axis) or the LEFT
   *   one (left / above). Ignored elsewhere — only the root's children have
   *   sides.
   */
  private _addMindmapNode(
    target: 'sibling' | 'child',
    bucket?: LayoutType.LEFT | LayoutType.RIGHT,
    // For a sibling: insert it before (above/left) or after (below/right) the
    // current node. Ignored for a child. This is the render-time fallback; the
    // real side is re-derived from live geometry below via `buttonDir`.
    siblingPos: 'before' | 'after' = 'after',
    // Physical side of the clicked button. Used to place the sibling on THAT
    // side regardless of layout / how the layout was reached.
    buttonDir?: Direction
  ) {
    const mindmap = this.current.group;

    if (!(mindmap instanceof MindmapElementModel)) return;

    const parent =
      target === 'sibling'
        ? (mindmap.getParentNode(this.current.id) ?? this.current)
        : this.current;

    const parentNode = mindmap.getNode(parent.id);

    if (!parentNode) return;

    // Decide before/after at click time from the CURRENT sibling geometry, never
    // from a value baked when the buttons were rendered (a layout switch would
    // leave that stale). See `_resolveSiblingSide`.
    const pos =
      target === 'sibling' && buttonDir !== undefined
        ? this._resolveSiblingSide(mindmap, parentNode, buttonDir, siblingPos)
        : siblingPos;

    const isRoot = mindmap.tree.id === this.current.id;
    const rootChild = target === 'child' && isRoot && bucket !== undefined;

    let newNode!: string;

    this.std.store.transact(() => {
      if (rootChild) {
        // Only the balanced layouts draw branches on both sides of the root, so
        // asking for the far side of a one-sided map has to switch it to the
        // balanced variant of the same axis — the same thing the Radial entry in
        // the layout menu does, and it re-lays out on its own (`layoutType` is
        // @watch-ed).
        //
        // Freeze every existing branch on the side it is ALREADY on, BEFORE the
        // layout flips — otherwise the halve-by-index fallback would fling half
        // the map across the root.
        mindmap.pinCurrentSides();

        const balanced = VERTICAL_MINDMAP_LAYOUTS.has(mindmap.layoutType)
          ? LayoutType.BALANCE_VERTICAL
          : LayoutType.BALANCE;

        if (mindmap.layoutType !== balanced) {
          mindmap.layoutType = balanced;
        }

        const side = bucket === LayoutType.RIGHT ? 'right' : 'left';

        newNode = mindmap.addNode(parentNode.id);
        mindmap.setSide(newNode, side);

        // Adding to a side that is folded shut should open it — otherwise the
        // node you just asked for is created invisible.
        if (mindmap.isSideCollapsed(side)) {
          mindmap.toggleCollapseSide(side);
        }
      } else {
        newNode = mindmap.addNode(
          parentNode.id,
          target === 'sibling' ? this.current.id : undefined,
          target === 'sibling' ? pos : undefined,
          undefined
        );
      }

      if (parentNode.detail.collapsed) {
        mindmap.toggleCollapse(parentNode);
      }
    });

    requestAnimationFrame(() => {
      mountShapeTextEditor(
        this.crud.getElementById(newNode) as ShapeElementModel,
        this.edgeless
      );
    });
  }

  /**
   * Pick 'before' / 'after' so the new sibling lands on the physical side of the
   * clicked button (`buttonDir`), read FRESH from the current sibling geometry at
   * click time — never a value baked when the buttons rendered, which a layout
   * switch would leave stale.
   *
   * `addNode(…, 'after')` inserts a higher sibling index; which way that moves the
   * new node on screen is NOT fixed per layout (a side's index order can run up or
   * down), but WITHIN one side of one parent it is strictly monotonic along the
   * spread axis. So: take the current node's own side (the balanced layouts pack
   * both sides into the parent's single index-ordered child list — restrict to the
   * matching `detail.side` so the order stays monotonic), then read from the real
   * positions which way a rising index moves. No layout/side table to drift out of
   * sync; a lone same-side child (nothing to measure) keeps the render-time hint.
   */
  private _resolveSiblingSide(
    mindmap: MindmapElementModel,
    parentNode: {
      children?: {
        id: string;
        element?: { x?: number; y?: number; w?: number; h?: number };
      }[];
    },
    buttonDir: Direction,
    fallback: 'before' | 'after'
  ): 'before' | 'after' {
    const horizontal =
      buttonDir === Direction.Top || buttonDir === Direction.Bottom;
    // Axis the siblings spread along: vertical for Top/Bottom, horizontal for
    // Left/Right. Center the coordinate so unequal node sizes can't skew it.
    const spread = (e?: { x?: number; y?: number; w?: number; h?: number }) =>
      !e
        ? NaN
        : horizontal
          ? (e.y ?? NaN) + (e.h ?? 0) / 2
          : (e.x ?? NaN) + (e.w ?? 0) / 2;
    const sideOf = (id: string): unknown => {
      try {
        return (mindmap.children as { get(k: string): { side?: unknown } })?.get(
          id
        )?.side;
      } catch {
        return undefined;
      }
    };

    let group = parentNode.children ?? [];
    const curSide = sideOf(this.current.id);
    if (curSide !== undefined && curSide !== null) {
      const same = group.filter(k => sideOf(k.id) === curSide);
      if (same.length >= 2) group = same;
    }
    if (group.length < 2) return fallback;

    const first = spread(group[0]?.element);
    const last = spread(group[group.length - 1]?.element);
    if (!Number.isFinite(first) || !Number.isFinite(last) || first === last) {
      return fallback;
    }

    // 'after' (higher index) moves toward the larger coord when the last same-side
    // sibling sits past the first. The click points toward the larger coord for a
    // Bottom / Right button.
    const afterTowardLarger = last > first;
    const buttonTowardLarger =
      buttonDir === Direction.Bottom || buttonDir === Direction.Right;
    return buttonTowardLarger === afterTowardLarger ? 'after' : 'before';
  }

  private _computeLine(
    type: Direction,
    curShape: ShapeElementModel,
    nextBound: Bound
  ) {
    const startBound = this.current.elementBound;
    const { startPosition, endPosition } = getPosition(type);
    const nextShape = {
      xywh: nextBound.serialize(),
      rotate: curShape.rotate,
      shapeType: curShape.shapeType,
    };
    const startPoint = curShape.getRelativePointLocation(startPosition);
    const endPoint = curShape.getRelativePointLocation.call(
      nextShape,
      endPosition
    );

    return this._pathGenerator.generateOrthogonalConnectorPath({
      startBound,
      endBound: nextBound,
      startPoint,
      endPoint,
    });
  }

  private _computeNextBound(type: Direction) {
    if (isShape(this.current)) {
      const connectedShapes = this._getConnectedElements(this.current).filter(
        e => e instanceof ShapeElementModel
      ) as ShapeElementModel[];
      return nextBound(type, this.current, connectedShapes);
    } else {
      const bound = this.current.elementBound;
      switch (type) {
        case Direction.Right: {
          bound.x += bound.w + MAIN_GAP;
          break;
        }
        case Direction.Bottom: {
          bound.y += bound.h + MAIN_GAP;
          break;
        }
        case Direction.Left: {
          bound.x -= bound.w + MAIN_GAP;
          break;
        }
        case Direction.Top: {
          bound.y -= bound.h + MAIN_GAP;
          break;
        }
      }
      return bound;
    }
  }

  private _createAutoCompletePanel(
    e: PointerEvent,
    connector: ConnectorElementModel
  ) {
    if (!this.canShowAutoComplete) return;

    const position = this.gfx.viewport.toModelCoord(e.clientX, e.clientY);
    const autoCompletePanel = new EdgelessAutoCompletePanel(
      position,
      this.edgeless,
      this.current,
      connector
    );

    this.edgeless.append(autoCompletePanel);
  }

  private _generateElementOnClick(type: Direction) {
    const { store } = this.edgeless;
    const bound = this._computeNextBound(type);
    const id = createEdgelessElement(this.edgeless, this.current, bound);
    if (!id) return;
    if (isShape(this.current)) {
      const { startPosition, endPosition } = getPosition(type);
      this._addConnector(
        {
          id: this.current.id,
          position: startPosition,
        },
        {
          id,
          position: endPosition,
        }
      );

      mountShapeTextEditor(
        this.crud.getElementById(id) as ShapeElementModel,
        this.edgeless
      );
    } else {
      const model = store.getModelById(id);
      if (!model) {
        return;
      }
      const [x, y] = this.gfx.viewport.toViewCoord(
        bound.center[0],
        bound.y + DEFAULT_NOTE_HEIGHT / 2
      );
      requestAnimationFrame(() => {
        handleNativeRangeAtPoint(x, y);
      });
    }

    this.gfx.selection.set({
      elements: [id],
      editing: true,
    });
    this.removeOverlay();
  }

  private _getConnectedElements(element: ShapeElementModel) {
    if (!this._surface) return [];

    return this._surface.getConnectors(element.id).reduce((prev, current) => {
      if (current.target.id === element.id && current.source.id) {
        prev.push(
          this.crud.getElementById(current.source.id) as ShapeElementModel
        );
      }
      if (current.source.id === element.id && current.target.id) {
        prev.push(
          this.crud.getElementById(current.target.id) as ShapeElementModel
        );
      }

      return prev;
    }, [] as ShapeElementModel[]);
  }

  private _getMindmapButtons() {
    const mindmap = this.current.group as MindmapElementModel;
    const mindmapDirection =
      this.current instanceof ShapeElementModel &&
      mindmap instanceof MindmapElementModel
        ? mindmap.getLayoutDir(this.current.id)
        : null;
    const isRoot = mindmap?.tree.id === this.current.id;
    const mindmapNode = mindmap.getNode(this.current.id);

    // [where the button sits / which way its icon points, what it creates,
    //  which root-child bucket (child only), and for a sibling whether it is
    //  inserted BEFORE or AFTER the current node].
    let buttons: [
      Direction,
      'child' | 'sibling',
      LayoutType.LEFT | LayoutType.RIGHT,
      ('before' | 'after')?,
    ][] = [];

    // The third element of each tuple names the BUCKET a new root child would
    // land in, which is what decides the side it is drawn on:
    //   RIGHT bucket -> `root.right` -> right of the root  (or below it, on the
    //                   vertical axis)
    //   LEFT  bucket -> `root.left`  -> left of the root   (or above it)
    // See `_cfgBalanceLayoutDir` in the mindmap model. For non-root nodes the
    // bucket is meaningless (only the root's children have sides) and the value
    // is used purely to mirror the icon.
    switch (mindmapDirection) {
      case LayoutType.LEFT:
        buttons = [[Direction.Left, 'child', LayoutType.LEFT]];

        if (isRoot) {
          buttons.push([Direction.Right, 'child', LayoutType.RIGHT]);
        } else {
          // Horizontal: siblings stack vertically — one above, one below. The
          // LEFT branch is laid out vertically MIRRORED vs the right, so the
          // upper slot is the LATER sibling and the lower slot the EARLIER one
          // (swapped relative to the RIGHT case below).
          buttons.push([Direction.Top, 'sibling', mindmapDirection, 'after']);
          buttons.push([Direction.Bottom, 'sibling', mindmapDirection, 'before']);
        }
        break;
      case LayoutType.RIGHT:
        buttons = [[Direction.Right, 'child', LayoutType.RIGHT]];

        if (isRoot) {
          buttons.push([Direction.Left, 'child', LayoutType.LEFT]);
        } else {
          buttons.push([Direction.Top, 'sibling', mindmapDirection, 'before']);
          buttons.push([Direction.Bottom, 'sibling', mindmapDirection, 'after']);
        }
        break;
      case LayoutType.BALANCE:
        buttons = [
          [Direction.Right, 'child', LayoutType.RIGHT],
          [Direction.Left, 'child', LayoutType.LEFT],
        ];
        break;
      // The vertical layouts are this build's addition. They are the horizontal
      // ones rotated a quarter turn: children hang off the top/bottom edge, so
      // siblings line up left-to-right instead of top-to-bottom. Without these
      // cases the switch fell through to `[]` and a vertical mind map showed no
      // add-node buttons at all.
      case LayoutType.TOP:
        // Children hang below the root, i.e. in the RIGHT bucket.
        buttons = [[Direction.Bottom, 'child', LayoutType.RIGHT]];

        if (isRoot) {
          buttons.push([Direction.Top, 'child', LayoutType.LEFT]);
        } else {
          // Vertical: siblings line up horizontally — one to the left (before),
          // one to the right (after).
          buttons.push([Direction.Left, 'sibling', LayoutType.RIGHT, 'before']);
          buttons.push([Direction.Right, 'sibling', LayoutType.RIGHT, 'after']);
        }
        break;
      case LayoutType.BOTTOM:
        // Children stack above the root, i.e. in the LEFT bucket.
        buttons = [[Direction.Top, 'child', LayoutType.LEFT]];

        if (isRoot) {
          buttons.push([Direction.Bottom, 'child', LayoutType.RIGHT]);
        } else {
          buttons.push([Direction.Left, 'sibling', LayoutType.RIGHT, 'before']);
          buttons.push([Direction.Right, 'sibling', LayoutType.RIGHT, 'after']);
        }
        break;
      case LayoutType.BALANCE_VERTICAL:
        buttons = [
          [Direction.Bottom, 'child', LayoutType.RIGHT],
          [Direction.Top, 'child', LayoutType.LEFT],
        ];
        break;
      default:
        buttons = [];
    }

    return buttons.length
      ? {
          mindmapNode,
          buttons,
        }
      : null;
  }

  private _initOverlay() {
    const surface = getSurfaceComponent(this.std);
    if (!surface) return;
    this._autoCompleteOverlay = new AutoCompleteOverlay(this.gfx);
    surface.renderer.addOverlay(this._autoCompleteOverlay);
  }

  private _renderArrow() {
    const isShape = this.current instanceof ShapeElementModel;
    const { selectedRect } = this;
    const { zoom } = this.gfx.viewport;
    const width = 72;
    const height = 44;

    // Auto-complete arrows for shape and note are different
    // Shape: right, bottom, left, top
    // Note: right, left
    const arrowDirections = isShape
      ? [Direction.Right, Direction.Bottom, Direction.Left, Direction.Top]
      : [Direction.Right, Direction.Left];
    const arrowMargin = isShape ? height / 2 : height * (2 / 3);
    const Arrows = arrowDirections.map(type => {
      let transform = '';

      const iconSize = { width: '16px', height: '16px' };
      const icon = (isShape ? ArrowUpBigIcon : PlusIcon)(iconSize);

      switch (type) {
        case Direction.Top:
          transform += `translate(${
            selectedRect.width / 2
          }px, ${-arrowMargin}px)`;
          break;
        case Direction.Right:
          transform += `translate(${selectedRect.width + arrowMargin}px, ${
            selectedRect.height / 2
          }px)`;

          isShape && (transform += `rotate(90deg)`);
          break;
        case Direction.Bottom:
          transform += `translate(${selectedRect.width / 2}px, ${
            selectedRect.height + arrowMargin
          }px)`;
          isShape && (transform += `rotate(180deg)`);
          break;
        case Direction.Left:
          transform += `translate(${-arrowMargin}px, ${
            selectedRect.height / 2
          }px)`;
          isShape && (transform += `rotate(-90deg)`);
          break;
      }
      transform += `translate(${-width / 2}px, ${-height / 2}px)`;
      const arrowWrapperClasses = classMap({
        'edgeless-auto-complete-arrow-wrapper': true,
        hidden: !isShape && type === Direction.Left && zoom >= 1.5,
      });

      return html`<div
        class=${arrowWrapperClasses}
        style=${styleMap({
          transform,
          transformOrigin: 'left top',
        })}
      >
        <div
          class="edgeless-auto-complete-arrow"
          @mouseenter=${() => {
            this._timer = setTimeout(() => {
              if (this.current instanceof ShapeElementModel) {
                const bound = this._computeNextBound(type);
                const path = this._computeLine(type, this.current, bound);
                this._showNextShape(
                  this.current,
                  bound,
                  path,
                  this.current.shapeType
                );
              }
            }, 300);
          }}
          @mouseleave=${() => {
            this.removeOverlay();
          }}
          @pointerdown=${(e: PointerEvent) => {
            this._onPointerDown(e, type);
          }}
        >
          ${icon}
        </div>
      </div>`;
    });

    return Arrows;
  }

  private _renderMindMapButtons() {
    const mindmapButtons = this._getMindmapButtons();

    if (!mindmapButtons) {
      return;
    }

    const { selectedRect } = this;
    const { zoom } = this.gfx.viewport;
    const mindmap = this.current.group as MindmapElementModel;
    const size = 26;
    const buttonMargin =
      (mindmapButtons.mindmapNode?.children.length ?? 0) > 0
        ? size / 2 + 32 * zoom
        : size / 2 + 6;
    const verticalMargin = size / 2 + 6;

    return mindmapButtons.buttons.map(type => {
      let transform = '';

      const [position, target, layout, siblingPos] = type;
      const icon = (target === 'child' ? SubNodeIcon : SiblingNodeIcon)({
        width: '16px',
        height: '16px',
      });

      // The collapse button already sits on the edge the branch leaves from, so
      // only the "child" button has to clear it. Which edge that is depends on
      // the layout, so key the margin off the button's purpose rather than its
      // direction — otherwise the vertical layouts push the wrong one out.
      const margin = target === 'child' ? buttonMargin : verticalMargin;

      const isLeftLayout = layout === LayoutType.LEFT;

      // Orient each icon toward its actual creation direction.
      //  - SubNodeIcon (child) defaults to pointing right → rotate it to face
      //    `position` (below → 90, above → -90, left → 180, right → 0).
      //  - SiblingNodeIcon defaults to "sibling below, with the parent connector
      //    on the left". before/after must differ only by a VERTICAL flip so the
      //    connector stays on the parent's side — a 180° spin (what it had) also
      //    flips it horizontally, which is the icon that looked wrong. A left-side
      //    branch (parent on the RIGHT) additionally mirrors horizontally. On the
      //    vertical axis siblings sit left/right, so the icon takes a quarter turn.
      let iconTransform: string;
      if (target === 'child') {
        const r =
          position === Direction.Bottom
            ? 90
            : position === Direction.Left
              ? 180
              : position === Direction.Top
                ? -90
                : 0;
        iconTransform = `rotate(${r}deg)`;
      } else if (position === Direction.Top || position === Direction.Bottom) {
        const fx = isLeftLayout ? -1 : 1;
        const fy = position === Direction.Top ? -1 : 1;
        iconTransform = `scale(${fx}, ${fy})`;
      } else {
        // Vertical layout: siblings sit left/right and the parent connector runs
        // vertically. Point the stem toward the parent — UP when the branch
        // grows downward (parent above), DOWN when it grows upward — and the
        // new-sibling indicator toward this button's own side (left/right).
        // Derive the parent's side from geometry (this node vs the root) so it
        // holds for TOP, BOTTOM and the split BALANCE_VERTICAL alike, falling
        // back to the layout type when the root position can't be read.
        let parentAbove = mindmap?.layoutType !== LayoutType.BOTTOM;
        try {
          const rootY = (mindmap?.tree?.element as { y?: number } | undefined)
            ?.y;
          const selfY = (this.current as { y?: number }).y;
          if (typeof rootY === 'number' && typeof selfY === 'number') {
            parentAbove = selfY >= rootY;
          }
        } catch {
          // keep the layoutType-based fallback
        }
        const rot = parentAbove ? 90 : -90;
        const mirror =
          (position === Direction.Right) === parentAbove ? 'scaleX(-1) ' : '';
        iconTransform = `${mirror}rotate(${rot}deg)`;
      }

      switch (position) {
        case Direction.Top:
          transform += `translate(${selectedRect.width / 2}px, ${-margin}px)`;
          break;
        case Direction.Bottom:
          transform += `translate(${selectedRect.width / 2}px, ${
            selectedRect.height + margin
          }px)`;
          break;
        case Direction.Right:
          transform += `translate(${selectedRect.width + margin}px, ${
            selectedRect.height / 2
          }px)`;
          break;
        case Direction.Left:
          transform += `translate(${-margin}px, ${selectedRect.height / 2}px)`;
          break;
      }

      transform += iconTransform;
      transform += `translate(${-size / 2}px, ${-size / 2}px)`;

      const arrowWrapperClasses = classMap({
        'edgeless-auto-complete-arrow-wrapper': true,
        hidden: position === Direction.Left && zoom >= 1.5,
        mindmap: true,
      });

      return html`<div
        class=${arrowWrapperClasses}
        style=${styleMap({
          transform,
          transformOrigin: 'left top',
        })}
      >
        <div
          class="edgeless-auto-complete-arrow"
          @pointerdown=${() => {
            this._addMindmapNode(target, layout, siblingPos, position);
          }}
        >
          ${icon}
        </div>
      </div>`;
    });
  }

  private _showNextShape(
    current: ShapeElementModel,
    bound: Bound,
    path: IVec[],
    targetType: ShapeType
  ) {
    const surface = getSurfaceComponent(this.std);
    if (!surface) return;

    this._autoCompleteOverlay.stroke = surface.renderer.getColorValue(
      current.strokeColor,
      DefaultTheme.shapeStrokeColor,
      true
    );
    this._autoCompleteOverlay.linePoints = path;
    this._autoCompleteOverlay.renderShape = ctx => {
      shapeMethods[targetType].draw(ctx, { ...bound, rotate: current.rotate });
    };
    surface.refresh();
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this._pathGenerator = new ConnectorPathGenerator({
      getElementById: id => this.crud.getElementById(id),
    });
    this._initOverlay();
  }

  override firstUpdated() {
    const { _disposables, edgeless, gfx } = this;

    _disposables.add(
      this.gfx.selection.slots.updated.subscribe(() => {
        this._autoCompleteOverlay.linePoints = [];
        this._autoCompleteOverlay.renderShape = null;
      })
    );

    _disposables.add(
      gfx.selection.slots.updated.subscribe(() => {
        this.requestUpdate();
      })
    );

    _disposables.add(() => this.removeOverlay());

    _disposables.add(
      edgeless.host.event.add('pointerMove', ctx => {
        const evt = ctx.get('pointerState');
        const [x, y] = gfx.viewport.toModelCoord(evt.x, evt.y);
        const elm = gfx.getElementByPoint(x, y);

        if (!elm) {
          this._isHover = false;
          return;
        }

        this._isHover = elm === this.current ? true : false;
      })
    );

    this.edgeless.handleEvent('dragStart', () => {
      this._isMoving = true;
    });
    this.edgeless.handleEvent('dragEnd', () => {
      this._isMoving = false;
    });
  }

  private _canAutoComplete() {
    const selection = this.gfx.selection;
    return (
      selection.selectedElements.length === 1 &&
      (selection.selectedElements[0] instanceof ShapeElementModel ||
        isNoteBlock(selection.selectedElements[0]))
    );
  }

  removeOverlay() {
    this._timer && clearTimeout(this._timer);
    const surface = getSurfaceComponent(this.std);
    if (!surface) return;
    surface.renderer.removeOverlay(this._autoCompleteOverlay);
  }

  override render() {
    const isShape = this.current instanceof ShapeElementModel;
    const isMindMap = this.current.group instanceof MindmapElementModel;

    if (
      this._isMoving ||
      (this._isHover && !isShape && !this._canAutoComplete())
    ) {
      this.removeOverlay();
      return nothing;
    }
    const { selectedRect } = this;

    return html`<div
      class="edgeless-auto-complete-container"
      style=${styleMap({
        top: selectedRect.top + 'px',
        left: selectedRect.left + 'px',
        width: selectedRect.width + 'px',
        height: selectedRect.height + 'px',
        transform: `rotate(${selectedRect.rotate}deg)`,
      })}
    >
      ${isMindMap ? this._renderMindMapButtons() : this._renderArrow()}
    </div>`;
  }

  @state()
  private accessor _isHover = true;

  @state()
  private accessor _isMoving = false;

  @property({ attribute: false })
  accessor current!: ShapeElementModel | NoteBlockModel;

  @property({ attribute: false })
  accessor edgeless!: BlockComponent;

  @property({ attribute: false })
  accessor selectedRect!: SelectedRect;

  @consume({
    context: stdContext,
  })
  accessor std!: BlockStdScope;
}
